import { AlertTriangle, Clock, Flame, TrendingDown } from "lucide-react";
import { fmtMoney } from "@/lib/format";

const Card = ({ icon: Icon, tone, title, children, testId }) => (
  <div className="bg-[#141414] border border-[#262626] rounded-sm p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-3.5 h-3.5" style={{ color: tone }} strokeWidth={1.8}/>
      <div className="kpi-label" style={{ color: tone }}>{title}</div>
    </div>
    {children}
  </div>
);

const EmptyLine = () => <div className="text-sm text-[#525252]">None</div>;

export const HealthInsights = ({ health }) => {
  const bottlenecks = health?.bottlenecks || [];
  const stalled = health?.stalled_deals || [];
  const highPotential = health?.high_potential || [];
  const atRisk = health?.at_risk || [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card icon={TrendingDown} tone="#FF9F0A" title="Funnel Bottlenecks" testId="health-bottlenecks">
        {bottlenecks.length === 0 ? <EmptyLine/> : (
          <ul className="space-y-2">
            {bottlenecks.map((b, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="font-mono-data text-[#FF9F0A] tabular-nums w-14 shrink-0">{b.conversion}%</span>
                <div>
                  <div className="text-white">{b.stage}</div>
                  <div className="text-xs text-[#A3A3A3] mt-0.5">{b.note}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card icon={Clock} tone="#A3A3A3" title={`Stalled Deals · >${health?.stalled_threshold_days ?? 14}d idle`} testId="health-stalled">
        {stalled.length === 0 ? <EmptyLine/> : (
          <ul className="space-y-2">
            {stalled.slice(0, 6).map((s, i) => (
              <li key={i} className="flex items-baseline justify-between text-sm border-b border-[#1F1F1F] pb-1.5 last:border-b-0">
                <div className="min-w-0">
                  <div className="text-white truncate">{s.account_name}</div>
                  <div className="kpi-label">{s.bdr_name} · {s.stage}</div>
                </div>
                <div className="font-mono-data text-[#FF9F0A] whitespace-nowrap ml-2">{s.days_idle}d</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card icon={Flame} tone="#32D74B" title="High-Potential Accounts · ≥$250K" testId="health-high-potential">
        {highPotential.length === 0 ? <EmptyLine/> : (
          <ul className="space-y-2">
            {highPotential.slice(0, 6).map((h, i) => (
              <li key={i} className="flex items-baseline justify-between text-sm border-b border-[#1F1F1F] pb-1.5 last:border-b-0">
                <div className="min-w-0">
                  <div className="text-white truncate">{h.account_name}</div>
                  <div className="kpi-label">{h.bdr_name} · {h.stage}</div>
                </div>
                <div className="font-mono-data text-[#32D74B] whitespace-nowrap ml-2">{fmtMoney(h.estimated_deal_value)}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card icon={AlertTriangle} tone="#FF453A" title="At-Risk Opportunities · Proposal >21d" testId="health-at-risk">
        {atRisk.length === 0 ? <EmptyLine/> : (
          <ul className="space-y-2">
            {atRisk.slice(0, 6).map((a, i) => (
              <li key={i} className="flex items-baseline justify-between text-sm border-b border-[#1F1F1F] pb-1.5 last:border-b-0">
                <div className="min-w-0">
                  <div className="text-white truncate">{a.account_name}</div>
                  <div className="kpi-label">{a.bdr_name} · {a.days_since_proposal}d since proposal</div>
                </div>
                <div className="font-mono-data text-[#FF453A] whitespace-nowrap ml-2">{fmtMoney(a.proposal_value)}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default HealthInsights;
